import React, { useEffect } from 'react'
import { usePathContext } from '#src/components/Path/PathContext'
import { FullBlockLoader, CircleLoader, Button, Image } from '@deal/components'
import { Experiment, Variation } from '@deal/experiment-js'
import numeral from 'numeral'
import times from 'lodash/times'
import truncate from 'lodash/truncate'
import { RecommendExpertsForPathFlowComponent, PathExpertFragment } from '#src/generated/graphql'
import { IdQuestionProps } from '../'
import styles from './styles.css'

interface ExpertProps {
  expert: PathExpertFragment
  onSelectExpert: (expertId: string) => void
}

const Expert: React.SFC<ExpertProps> = ({ expert, onSelectExpert }) => {
  const pathContext = usePathContext()

  const expertStats = expert.expertStats ? expert.expertStats : null
  const averageRating =
    expert.expertStats && expert.expertStats.averageRating
      ? Math.ceil(expert.expertStats.averageRating)
      : null

  return (
    <div className={styles.expert}>
      {expert.expertProfile && expert.expertProfile.profilePhoto && (
        <div className={styles.expertPhoto}>
          <Image
            src={expert.expertProfile.profilePhoto.url}
            className={styles.profilePhoto}
            width={375}
            height={250}
            imgixParams={{ fit: 'fill', fill: 'blur' }}
          />
          {expertStats && expertStats.totalMessagesSent > 0 && (
            <div className={styles.messages}>
              {numeral(expertStats.totalMessagesSent).format('0,0')}{' '}
              {expertStats.totalMessagesSent === 1 ? 'message' : 'messages'}
            </div>
          )}
          {expertStats && expertStats.totalReviews > 0 && (
            <div className={styles.totalReviews}>
              {numeral(expertStats.totalReviews).format('0,0')}{' '}
              {expertStats.totalReviews === 1 ? 'review' : 'reviews'}
            </div>
          )}{' '}
        </div>
      )}
      <div className={styles.copy}>
        <div className={styles.topRow}>
          <div className={styles.name}>{expert.displayName}</div>
          {averageRating && (
            <div className={styles.rating}>
              {times(Math.round(averageRating)).map(() => (
                <span className={styles.star} />
              ))}
            </div>
          )}
        </div>
        {expert.expertProfile && (
          <>
            <div className={styles.background}>
              {truncate(expert.expertProfile.background, {
                length: 150
              })}
            </div>

            {expert.expertProfile.highlights && (
              <>
                {expert.expertProfile.highlights.map(highlight => (
                  <div className={styles.highlight} key={highlight.value}>
                    <div className={styles.highlightName}>{highlight.secondaryValue}</div>
                    <div className={styles.highlightValues}>
                      <div className={styles.highlightValue}>{highlight.value}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
      <div className={styles.button}>
        <Button
          type="button"
          disabled={pathContext && pathContext.isSubmitting}
          onClick={() => onSelectExpert(expert.id)}
        >
          <Experiment id="expert-selection-buttons" default="v3">
            <Variation id="v1">
              Pick <span className={styles.firstName}> {expert.displayName}</span>
            </Variation>
            <Variation id="v2">
              Work with <span className={styles.firstName}> {expert.displayName}</span>
            </Variation>
            <Variation id="v3">
              Chat with <span className={styles.firstName}> {expert.displayName}</span>
            </Variation>
          </Experiment>
        </Button>
      </div>
    </div>
  )
}

const ExpertSelectionQuestion: React.SFC<IdQuestionProps> = ({
  question,
  onFieldValidityChanged,
  onFieldValueChanged
}) => {
  const pathContext = usePathContext()

  useEffect(() => {
    /**
     * It's important to make sure this question is never required since no expert
     * id may be returned from this query, leaving the user stuck.
     */
    if (!question.required) {
      onFieldValidityChanged(true)
    }
  }, [])

  const handleSelectExpert = (expertId: string) => {
    onFieldValueChanged({ value: expertId })
    pathContext && pathContext.onPageSubmitted()
  }

  return (
    <RecommendExpertsForPathFlowComponent
      variables={{
        pathFlowId: pathContext && pathContext.pathFlow.id
      }}
    >
      {({ data }) => {
        if (data && data.recommendExpertsForPathFlow) {
          if (data.recommendExpertsForPathFlow.length === 0) {
            // If there are no experts for this category, just submit the page
            pathContext && pathContext.onPageSubmitted()

            return <FullBlockLoader loader={CircleLoader} />
          }

          return (
            <div className={styles.container}>
              {data.recommendExpertsForPathFlow.map(recommendation => (
                <Expert
                  key={recommendation.expert.id}
                  expert={recommendation.expert}
                  onSelectExpert={handleSelectExpert}
                />
              ))}
            </div>
          )
        }

        return <FullBlockLoader loader={CircleLoader} />
      }}
    </RecommendExpertsForPathFlowComponent>
  )
}

export default ExpertSelectionQuestion
